import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { LogIn } from 'lucide-react';
import { useUserStore } from '../../lib/userStore';
import { useLeagueStore } from '../../lib/leagueStore';
import { useLanguageStore } from '../../lib/languageStore';
import { translate } from '../../lib/translations';
import { useEmbedStore } from '../../lib/embedStore';
import { isTslhTenant } from '../../lib/tenantStore';
import { TSLH_LEAGUE_ID } from '../../lib/tenantSession';
import { joinTslhLeagueAndRedirect } from '../../lib/tenantJoin';
import { recordUserLogin } from '../../lib/loginTracking';

export function LoginForm() {
  const navigate = useNavigate();
  const isEmbed = useEmbedStore((state) => state.isEmbed);
  const setUsername = useUserStore((state) => state.setUsername);
  const setUserId = useUserStore((state) => state.setUserId);
  const setDateOfBirth = useUserStore((state) => state.setDateOfBirth);
  const setCurrentLeagueId = useUserStore((state) => state.setCurrentLeagueId);
  const setSelectedLeague = useLeagueStore((state) => state.setSelectedLeague);
  const { language } = useLanguageStore();
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [rememberMe, setRememberMe] = useState(true); // Par défaut à true pour rester connecté

  // Initialize checkbox from existing preference
  React.useEffect(() => {
    try {
      const stored = window.localStorage.getItem('remember_me');
      // Si aucune préférence n'est sauvegardée, mettre à true par défaut
      if (stored === null) {
        setRememberMe(true);
        window.localStorage.setItem('remember_me', 'true');
      } else {
        setRememberMe(stored === 'true');
      }
    } catch (e) {
      // ignore
    }
  }, []);
  const [resetData, setResetData] = useState({
    email: '',
    newPassword: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.rpc('login_user', {
        p_email: formData.email,
        p_password: formData.password,
        p_context_league_id: isTslhTenant() ? TSLH_LEAGUE_ID : null,
      });

      if (error) throw error;

      const user = Array.isArray(data) && data.length > 0 ? data[0] : null;

      if (!user || !user.username) {
        throw new Error('Invalid email or password');
      }

      // Save the remember preference first so the store persist uses the right storage
      try {
        window.localStorage.setItem('remember_me', rememberMe ? 'true' : 'false');
      } catch (e) {
        console.warn('Could not persist remember_me flag', e);
      }

      // Si c'est un utilisateur différent, réinitialiser la ligue sélectionnée.
      // On utilise localStorage pour résister à l'expiration de session (sessionStorage vidé).
      try {
        const lastUserId = window.localStorage.getItem('last_login_user_id');
        const isDifferentUser = lastUserId !== null && parseInt(lastUserId, 10) !== user.user_id;
        if (isDifferentUser) {
          setCurrentLeagueId(null);
          setSelectedLeague(null);
        }
        window.localStorage.setItem('last_login_user_id', String(user.user_id));
      } catch (e) {
        // ignore storage errors
      }

      // Then update the user store so Zustand persist picks the correct storage
      setUsername(user.username);
      setUserId(user.user_id);
      setDateOfBirth(user.date_of_birth || null);

      // Enregistrer la connexion de l'utilisateur (non-bloquant)
      await recordUserLogin(user.user_id, user.username);

      toast.success(`${translate('welcome.user', language, { username: user.username })}`);

      // Tenant TSLH : auto-join la ligue 152 et rediriger vers /build-my-team ou /my-office
      // selon l'état de l'équipe. Ce chemin ne touche PAS Arsenal/OIDC.
      if (isTslhTenant()) {
        await joinTslhLeagueAndRedirect(user.user_id, navigate);
        return;
      }

      navigate(isEmbed ? '/standings' : '/my-office');
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error('Invalid email or password');
      setUsername(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card-body">
      {resetMode ? (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setLoading(true);
            try {
              // En contexte TSLH, ne réinitialiser que le compte membre de 152
              // pour ne pas changer le mot de passe des autres comptes du même email.
              let targetUserId: number | null = null;
              if (isTslhTenant()) {
                const { data: candidates, error: candidatesError } = await supabase
                  .from('users')
                  .select('user_id')
                  .eq('email', resetData.email);
                if (candidatesError) throw candidatesError;

                const candidateIds = (candidates ?? []).map((u) => u.user_id);
                if (candidateIds.length > 0) {
                  const { data: membership, error: membershipError } = await supabase
                    .from('leagues_users')
                    .select('user_id')
                    .eq('league_id', TSLH_LEAGUE_ID)
                    .in('user_id', candidateIds)
                    .maybeSingle();
                  if (membershipError && membershipError.code !== 'PGRST116') throw membershipError;
                  targetUserId = membership?.user_id ?? null;
                }

                if (!targetUserId) {
                  toast.error(translate('email.not.found', language));
                  return;
                }
              }

              const updateQuery = supabase.from('users').update({ password: resetData.newPassword });
              const { data, error } = targetUserId
                ? await updateQuery.eq('user_id', targetUserId).select('user_id')
                : await updateQuery.eq('email', resetData.email).select('user_id');

              if (error) throw error;

              const updatedCount = Array.isArray(data) ? data.length : 0;
              if (updatedCount === 0) {
                toast.error(translate('email.not.found', language));
              } else {
                toast.success(translate('password.updated', language));
                // Optionally prefill login email
                setFormData({ email: resetData.email, password: '' });
                setResetMode(false);
              }
            } catch (err) {
              console.error('Reset password error:', err);
              toast.error(translate('password.update.failed', language));
            } finally {
              setLoading(false);
            }
          }}
        >
          <div className="mb-3">
            <label htmlFor="resetEmail" className="form-label">
              {translate('email.address', language)}
            </label>
            <input
              type="email"
              className="form-control"
              id="resetEmail"
              value={resetData.email}
              onChange={(e) => setResetData({ ...resetData, email: e.target.value })}
              required
            />
          </div>

          <div className="mb-4">
            <label htmlFor="newPassword" className="form-label">
              {translate('new.password', language)}
            </label>
            <input
              type="password"
              className="form-control"
              id="newPassword"
              value={resetData.newPassword}
              onChange={(e) => setResetData({ ...resetData, newPassword: e.target.value })}
              required
            />
          </div>

          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary w-50"
              onClick={() => setResetMode(false)}
              disabled={loading}
            >
              {translate('back', language)}
            </button>
            <button
              type="submit"
              className="btn btn-primary w-50"
              disabled={loading}
            >
              {loading ? translate('updating.password', language) : translate('reset.password', language)}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="loginEmail" className="form-label">
              {translate('email.address', language)}
            </label>
            <input
              type="email"
              className="form-control"
              id="loginEmail"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              required
            />
          </div>

          <div className="mb-2">
            <label htmlFor="loginPassword" className="form-label">
              {translate('password', language)}
            </label>
            <input
              type="password"
              className="form-control"
              id="loginPassword"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              required
            />
          </div>

          <div className="mb-4 text-end">
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={() => setResetMode(true)}
            >
              {translate('forgot.password', language)}
            </button>
          </div>

          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              value=""
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="rememberMe">
              {translate('remember.me', language)}
            </label>
          </div>

          <button
            type="submit"
            className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
            disabled={loading}
          >
            <LogIn size={20} />
            {loading ? translate('logging.in', language) : translate('login', language)}
          </button>
        </form>
      )}
    </div>
  );
}
